/**
 * Commodity type options for the call-off delivery dropdowns.
 *
 * These modals used to build their options out of whatever rows happened to be
 * loaded, so a configured commodity type with no order or stock line on screen
 * was simply absent from the filter. This loads the full configured list
 * instead, the way the Stock Planning filter panel does.
 *
 * Pages are walked rather than fetched in one request: the backend clamps
 * page_size, so a single call silently truncates the list.
 *
 * The loaded rows' own values are merged on top. A commodity type that has
 * since been disabled can still sit on parts already on screen, and dropping it
 * would leave those rows unfilterable — a worse fault than the one this fixes.
 *
 * @param presentInRows - Commodity type names found on the currently loaded
 *   rows. Memoise it in the caller: a fresh array each render re-sorts the list
 *   every time.
 * @returns De-duplicated names, sorted alphabetically. Empty while the request
 *   is in flight, and on failure the dropdown falls back to the row values
 *   alone — the grid stays usable, which is why the error is swallowed rather
 *   than surfaced.
 */
import { useState, useEffect, useMemo } from 'react';
import { purchasingAPI, fetchAllPages } from '../../../lib/api';

export function useCommodityTypeOptions(presentInRows: string[]): string[] {
  const [configured, setConfigured] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadCommodityTypes = async () => {
      try {
        const list = await fetchAllPages<{ name: string }>(
          (page) => purchasingAPI.getCommodityTypes({ enabled: true, page_size: 100, page }),
        );
        if (!cancelled) setConfigured(list.map(ct => ct.name).filter(Boolean));
      } catch {
        // Silently fail — commodity type is an optional filter.
      }
    };
    loadCommodityTypes();
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => {
    const names = Array.from(new Set([...configured, ...presentInRows].filter(Boolean)));
    names.sort((a, b) => a.localeCompare(b));
    return names;
  }, [configured, presentInRows]);
}
